import { FC, useEffect, useRef, memo } from 'react';
import styled, { css } from 'styled-components';
import { X } from 'lucide-react';

import { useAuthUser } from '@redux/selectors/auth';
import theme from 'theme';
import DynamicChat from '@components/show/dynamicChat';
import { PremierText } from '@components/text';

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 20;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;

    ${({ $isOpen }) =>
      $isOpen &&
      css`
        opacity: 1;
        pointer-events: auto;
      `}
  }
`;

const ChatContainer = styled.div<{ $isOpen: boolean }>`
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  background-color: ${({ theme }) => theme.colors.black};
  border-top: 1px solid ${({ theme }) => theme.colors.grey[700]};
  z-index: 25;
  transform: translateY(100%);
  transition: transform 0.3s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: flex;
    flex-direction: column;

    ${({ $isOpen }) =>
      $isOpen &&
      css`
        transform: translateY(0);
      `}
  }
`;

const ChatHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
`;

const ChatTitle = styled(PremierText)`
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fonts.fontSize.lg};
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ChatContent = styled.div`
  flex: 1;
  overflow: hidden;
`;

type Props = {
  showId: string;
  isOpen: boolean;
  onClose: () => void;
};

const MobileChat: FC<Props> = ({ showId, isOpen, onClose }) => {
  const { isAnonymous } = useAuthUser();
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  return (
    <>
      <Overlay ref={overlayRef} $isOpen={isOpen} onClick={handleOverlayClick} />

      <ChatContainer $isOpen={isOpen}>
        <ChatHeader>
          <ChatTitle>Chat</ChatTitle>

          <CloseButton onClick={onClose}>
            <X size={20} color={theme.colors.white} />
          </CloseButton>
        </ChatHeader>

        <ChatContent>
          <DynamicChat showId={showId} isShownChatForm={!isAnonymous} />
        </ChatContent>
      </ChatContainer>
    </>
  );
};

export default memo(MobileChat);
